import dayjs from 'dayjs';
import type {
  V2PvMonitorPerformanceOverviewPostResponse,
  V2PvMonitorPredictOverviewPostResponse,
  V2PvMonitorSkyImagerMonitorPostResponse,
  V2PvMonitorWeatherStationDataPostResponse,
} from '@maxtropy/dmes-apis-v2';
import type { Point, SkyCamera } from './type';

const formatTime = (timestamp?: number) => (timestamp ? dayjs(timestamp).format('H:mm') : '');

const formatDateTime = (timestamp?: number) => (timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm') : '');

const formatNullableNumber = (value?: number | null, digits = 2) => (value == null ? '--' : value.toFixed(digits));

const formatNullablePercent = (value?: number | null, digits = 2) =>
  value == null ? '--' : `${(value * 100).toFixed(digits)}%`;

const formatNullableNumberWithUnit = (value?: number | null, digits = 2, unit = '') =>
  value == null ? '--' : `${value.toFixed(digits)}${unit}`;

/**
 * 格式化发电量数值（带单位换算）
 * 规则：
 * 1. 后端数据单位为 kWh
 * 2. 如果 > 1000，换算为 MWh（除以1000）
 * 3. 如果换算后还 > 10000，则显示为 "XX.XX万MWh"
 */
const formatPowerGeneration = (value?: number | null, digits = 2): string => {
  if (value == null) {
    return '--';
  }

  // 如果值大于1000，换算为MWh
  if (value > 1000) {
    const mwhValue = value / 1000;

    // 如果换算后的MWh值还大于10000，显示为"XX.XX万MWh"
    if (mwhValue > 10000) {
      const wanMwh = mwhValue / 10000;
      return `${wanMwh.toFixed(digits)}万MWh`;
    }

    return `${mwhValue.toFixed(digits)}MWh`;
  }

  // 值小于等于1000，保持kWh单位
  return `${value.toFixed(digits)}kWh`;
};

const getCommStatus = (commStatus?: 1 | 0) => ({
  commStatusText: commStatus === 0 ? '通讯异常' : '通讯正常',
  isCommAbnormal: commStatus === 0,
});

const toPoints = (points?: { ts?: number; value?: number | null }[]): Point[] =>
  (points ?? []).map(item => ({
    time: formatTime(item.ts),
    value: item.value ?? null,
  }));

const emptyWeatherData = {
  cards: [
    { label: '水平总辐射', value: '--', unit: 'W/m²', ...getCommStatus() },
    { label: '环境温度', value: '--', unit: '℃', ...getCommStatus() },
    { label: '风速', value: '--', unit: 'm/s', ...getCommStatus() },
    { label: '湿度', value: '--', unit: '%', ...getCommStatus() },
  ],
  ghi: [],
  temperature: [],
};

const emptyPerformanceData = {
  cards: [
    { label: 'PR', value: '--', icon: 'pr' },
    { label: '实际发电量', value: '--', icon: 'power' },
    { label: '理论发电量', value: '--', icon: 'solar' },
  ],
  line: [],
  table: [],
};

export const adaptSkyMonitorData = (data?: V2PvMonitorSkyImagerMonitorPostResponse): SkyCamera[] => {
  const devices = data?.devices ?? [];

  if (!devices.length) {
    return [];
  }

  return devices.map(device => ({
    name: device.deviceName ?? device.deviceCode ?? '--',
    status: device.status === 1 ? '在线' : '离线',
    indicators: [
      {
        label: '总云量',
        value: formatNullableNumberWithUnit(device.totalCloud, 2, ' %'),
        ...getCommStatus(device.commStatus),
      },
      {
        label: '厚云',
        value: formatNullableNumberWithUnit(device.thickCloud, 2, ' %'),
        ...getCommStatus(device.commStatus),
      },
      {
        label: '薄云',
        value: formatNullableNumberWithUnit(device.thinCloud, 2, ' %'),
        ...getCommStatus(device.commStatus),
      },
    ],
    totalCloud: toPoints(device.totalCloudPoints),
    visibleCloud: (device.thickCloudPoints ?? []).map((item, index) => ({
      time: formatTime(item.ts),
      thick: item.value ?? null,
      thin: device.thinCloudPoints?.[index]?.value ?? null,
    })),
  }));
};

export const adaptForecastData = (data?: V2PvMonitorPredictOverviewPostResponse) => {
  if (!data) {
    return {
      actual: [],
      predict: [],
      warning: '',
      correction: [
        { label: 'MAPE', value: '--' },
        { label: 'RMSE', value: '--' },
      ],
    };
  }

  return {
    actual: toPoints(data.actualPoints),
    predict: toPoints(data.predictPoints),
    warning: data.riskMessage ?? '',
    correction: [
      { label: 'MAPE', value: formatNullablePercent(data.mape) },
      { label: 'RMSE', value: formatNullablePercent(data.rmse) },
    ],
  };
};

export const adaptWeatherData = (data?: V2PvMonitorWeatherStationDataPostResponse) => {
  if (!data) {
    return emptyWeatherData;
  }

  const commStatus = getCommStatus(data.commStatus);

  return {
    cards: [
      {
        label: '水平总辐射',
        value: formatNullableNumber(data.horizontalTotalRadiation),
        unit: 'W/m²',
        ...commStatus,
      },
      { label: '环境温度', value: formatNullableNumber(data.temperature), unit: '℃', ...commStatus },
      { label: '风速', value: formatNullableNumber(data.windSpeed), unit: 'm/s', ...commStatus },
      { label: '湿度', value: formatNullableNumber(data.humidity), unit: '%', ...commStatus },
    ],
    ghi: toPoints(data.irradiancePoints),
    temperature: toPoints(data.temperaturePoints),
  };
};

export const adaptPerformanceData = (data?: V2PvMonitorPerformanceOverviewPostResponse) => {
  if (!data) {
    return emptyPerformanceData;
  }

  return {
    cards: [
      { label: 'PR', value: formatNullablePercent(data.pr), icon: 'pr' },
      { label: '实际发电量', value: formatPowerGeneration(data.actualPower, 2), icon: 'power' },
      { label: '理论发电量', value: formatPowerGeneration(data.theoreticalPower, 2), icon: 'solar' },
    ],
    line: (data.prTrend ?? []).map(item => ({
      time: formatTime(item.ts),
      value: item.pr == null ? null : item.pr * 100,
    })),
    table: (data.prList ?? []).map(item => ({
      time: formatDateTime(item.time),
      value: item.pr == null ? '--' : `${(item.pr * 100).toFixed(2)}%`,
    })),
  };
};
